import { useSelector, shallowEqual } from 'react-redux';

import { PARTNERS, PAYMENT_METHODS } from 'config/app-config';
import { selectFuelType } from 'state/fuel-type/fuel-type-slice';
import { usePartner } from 'components/app/routes/usePartner';
import { useMeterType } from 'hooks/useMeterType';
import { useHasInitialState } from 'components/direct-sales/energy-needs/hooks/useHasInitialState';

/**
 * A matrix representing payment options based on various conditions.
 * Each row corresponds to a specific configuration of conditions and the available payment methods.
 *
 * Columns:
 * 1. `isElectric`: (boolean) Indicates if the option is for electric supply.
 * 2. `isGas`: (boolean) Indicates if the option is for gas supply.
 * 3. `isElectricPrepay`: (boolean|null) Indicates if the electric supply is prepay (true), not prepay (false), or not applicable (null).
 * 4. `isTOUElectricityMeter`: (boolean|null) Indicates if the electric supply has a smart meter (true), no smart meter (false), or not applicable (null).
 * 5. `isGasPrepay`: (boolean|null) Indicates if the gas supply is prepay (true), not prepay (false), or not applicable (null).
 * 6. `isTOUGasMeter`: (boolean|null) Indicates if the gas supply has a smart meter (true), no smart meter (false), or not applicable (null).
 * 7. `availablePaymentMethods`: (Array) A list of available payment methods for the given configuration.
 *    Possible values include:
 *    - `PAYMENT_METHODS.PREPAY`
 *    - `PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT`
 *    - `PAYMENT_METHODS.CHECK`
 */
type PaymentOptions = typeof PAYMENT_METHODS[keyof typeof PAYMENT_METHODS][];
type PaymentOptionRow = [
  boolean | null,
  boolean | null,
  boolean | null,
  boolean | null,
  boolean | null,
  boolean | null,
  PaymentOptions
];

// prettier-ignore
const PAYMENT_OPTION_MAPPING: PaymentOptionRow[] = [

// Electric only
// [isElectric, isGas, isElectricPrepay, isTOUElectricityMeter, isGasPrepay, isTOUGasMeter,  availablePaymentMethods]
  [true,        null,  true,             false,                 null,        null,           [PAYMENT_METHODS.PREPAY]],
  [true,        null,  false,            true,                  null,        null,           [PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT, PAYMENT_METHODS.CHECK, PAYMENT_METHODS.PREPAY]],
  [true,        null,  false,            false,                 null,        null,           [PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT, PAYMENT_METHODS.CHECK]],

// Gas only
// [isElectric, isGas, isElectricPrepay, isTOUElectricityMeter, isGasPrepay, isTOUGasMeter,  availablePaymentMethods]
  [null,       true,   null,             null,                  true,        false,          [PAYMENT_METHODS.PREPAY]],
  [null,       true,   null,             null,                  false,       true,           [PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT, PAYMENT_METHODS.CHECK, PAYMENT_METHODS.PREPAY]],
  [null,       true,   null,             null,                  false,       false,          [PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT, PAYMENT_METHODS.CHECK]],

// Dual fuel
// [isElectric, isGas, isElectricPrepay, isTOUElectricityMeter, isGasPrepay, isTOUGasMeter,  availablePaymentMethods]
  [true,        true,  true,             false,                 true,        false,          [PAYMENT_METHODS.PREPAY]],
  [true,        true,  false,            true,                  false,       true,           [PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT, PAYMENT_METHODS.CHECK, PAYMENT_METHODS.PREPAY]],
  [true,        true,  false,            false,                 false,       false,          [PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT, PAYMENT_METHODS.CHECK]],
  [true,        true,  false,            false,                 false,       true,           [PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT, PAYMENT_METHODS.CHECK]],
  [true,        true,  false,            true,                  false,       false,          [PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT, PAYMENT_METHODS.CHECK]]
];

export const usePaymentOptions = () => {
  const { isDSJourney, activePartner } = usePartner();
  const isMoveInJourney = activePartner[PARTNERS.MOVE_IN];

  const { electric, gas } = useSelector(selectFuelType, shallowEqual);
  const {
    hasElectricityPrepayMeter,
    hasGasPrepayMeter,
    hasElectricityTOUMeter,
    hasGasTOUMeter
  } = useMeterType();
  const { fuel: hasFuelInitialState } = useHasInitialState();

  // [isElectric, isGas, isElectricPrepay, isTOUElectricityMeter, isGasPrepay, isTOUGasMeter, availablePaymentMethods]
  const toMatch = [
    hasFuelInitialState ? true : electric,
    hasFuelInitialState ? true : gas,
    hasElectricityPrepayMeter,
    hasElectricityTOUMeter,
    hasGasPrepayMeter,
    hasGasTOUMeter
  ].map((condition) => !!condition);

  const matchedRow = PAYMENT_OPTION_MAPPING.find((row) => {
    const newRow = [...row];
    newRow.pop(); // Remove the last element which is the availablePaymentMethods
    const rowConditions = newRow.map((condition) => !!condition);
    const rowConditionsJsonString = JSON.stringify(rowConditions);
    const toMatchJsonString = JSON.stringify(toMatch);

    const isMatch = rowConditionsJsonString === toMatchJsonString;

    return isMatch;
  });

  if (!matchedRow) {
    return [PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT];
  }

  const paymentOptions = [...matchedRow].pop() as PaymentOptions;

  if (isDSJourney) {
    return paymentOptions.filter((option) => {
      return option !== PAYMENT_METHODS.CHECK;
    });
  }

  if (isMoveInJourney) {
    return paymentOptions.filter((option) => {
      return option !== PAYMENT_METHODS.PREPAY;
    });
  }

  return paymentOptions;
};
